import { db } from '../lib/firebase';
import { collection, doc, addDoc, getDoc, getDocs, query, where, setDoc, updateDoc, serverTimestamp, increment } from 'firebase/firestore';
import { generateUniqueReferralCode } from './referrals';

export interface Marketer {
  id?: string;
  name: string;
  email?: string | null;
  phone?: string | null;
  referralCode: string;
  referralsCount?: number;
  createdAt?: any; // Firestore Timestamp
}

export interface MarketerReferralRecord {
  userId: string;
  name?: string | null;
  email?: string | null;
  createdAt: any; // Firestore Timestamp
}

async function generateUniqueReferralCodeForMarketer(): Promise<string> {
  // Try users first via existing generator
  for (let i = 0; i < 5; i++) {
    const code = await generateUniqueReferralCode();
    const qRef = query(collection(db, 'marketers'), where('referralCode', '==', code));
    const snap = await getDocs(qRef);
    if (snap.empty) return code;
  }
  // Fallback include timestamp fragment
  const base = await generateUniqueReferralCode();
  return base + String(Date.now()).slice(-2);
}

export async function createMarketer(payload: { name: string; email?: string; phone?: string }): Promise<Marketer & { id: string }> {
  const referralCode = await generateUniqueReferralCodeForMarketer();
  const docRef = await addDoc(collection(db, 'marketers'), {
    name: payload.name,
    email: payload.email || null,
    phone: payload.phone || null,
    referralCode,
    referralsCount: 0,
    createdAt: serverTimestamp(),
  } as Marketer);
  return { id: docRef.id, name: payload.name, email: payload.email || null, phone: payload.phone || null, referralCode, referralsCount: 0 };
}

export async function getMarketers(): Promise<Marketer[]> {
  const snap = await getDocs(collection(db, 'marketers'));
  return snap.docs.map(d => ({ id: d.id, ...(d.data() as Marketer) }));
}

export async function getMarketerByReferralCode(code: string): Promise<Marketer | null> {
  const qRef = query(collection(db, 'marketers'), where('referralCode', '==', code));
  const snap = await getDocs(qRef);
  if (!snap.empty) {
    const d = snap.docs[0];
    return { id: d.id, ...(d.data() as Marketer) };
  }
  return null;
}

export async function getMarketerById(id: string): Promise<Marketer | null> {
  const snap = await getDoc(doc(db, 'marketers', id));
  return snap.exists() ? ({ id: snap.id, ...(snap.data() as Marketer) }) : null;
}

export async function recordMarketerReferral(marketerId: string, referredUid: string): Promise<void> {
  // Get referred user's basic info
  const referredSnap = await getDoc(doc(db, 'users', referredUid));
  const referredData = referredSnap.exists() ? (referredSnap.data() as any) : {};

  // Write subcollection record under marketer
  const subRef = collection(doc(db, 'marketers', marketerId), 'referrals');
  await addDoc(subRef, {
    userId: referredUid,
    name: referredData.fullName || null,
    email: referredData.email || null,
    createdAt: serverTimestamp(),
  } as MarketerReferralRecord);

  // Increment counter on marketer profile
  try {
    await updateDoc(doc(db, 'marketers', marketerId), { referralsCount: increment(1) });
  } catch {
    await setDoc(doc(db, 'marketers', marketerId), { referralsCount: increment(1) }, { merge: true });
  }
}